import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Check, Sparkles, Zap, QrCode, Bell, ChefHat, BarChart3, ArrowRight,
    Smartphone, Languages, Info, Wheat, HandHelping, Receipt, Plus,
    UtensilsCrossed, Image, Store, Clock, Armchair, UserCheck, ClipboardList,
    Headphones, Split, LayoutGrid, Users, KeyRound, Shield, Building2, TrendingUp,
    Edit3, Globe, Menu, X,
    Wand2, FileSpreadsheet, Layers, EyeOff, CheckSquare, Copy, Compass,
} from 'lucide-react';
import { APP_NAME } from '../lib/brand';
import { Logo } from '../components/Logo';
import { SitePublicFooter } from '../components/SitePublicFooter';
import { TestimonialMarquee } from '../components/TestimonialMarquee';
import { useSiteSettings } from '../lib/useSiteSettings';
import { PLANS as STATIC_PLANS } from '../lib/plans';
import { useLivePlans } from '../lib/useLivePlans';
import { BillingCyclePicker } from '../components/BillingCyclePicker';
import { PlanPriceDisplay } from '../components/PlanPriceDisplay';

const Plan = ({ plan, featured, testId, billingCycle }) => (
    <div
        className="panel panel-hover plan-card"
        data-testid={testId}
        style={{
            border: (featured ?? plan.featured) ? '1px solid rgba(245,158,11,0.5)' : undefined,
            boxShadow: (featured ?? plan.featured) ? '0 0 50px rgba(245,158,11,0.15)' : undefined,
        }}>
        {(featured ?? plan.featured) && (
            <div className="badge badge-amber plan-card__badge">
                <Sparkles size={12} /> Cel mai popular
            </div>
        )}
        <div className="plan-card__head">
            <div className="kicker">{plan.name}</div>
            <div className="plan-card__price">
                <PlanPriceDisplay plan={plan} cycleId={billingCycle} size="lg" variant="card" />
            </div>
        </div>
        <div className="plan-card__divider" />
        <ul className="plan-card__features">
            {plan.perks.map((p, i) => (
                <li key={i}>
                    <Check size={14} color="var(--neon-teal)" /> {p}
                </li>
            ))}
        </ul>
        <Link
            to="/register"
            className={`plan-card__cta ${(featured ?? plan.featured) ? 'btn btn-primary' : 'btn btn-secondary'}`}
            data-testid={`${testId}-cta`}
        >
            Începe cu {plan.name} <ArrowRight size={16} />
        </Link>
    </div>
);

const Feature = ({ icon: Icon, title, body, color }) => (
    <div className="panel panel-hover">
        <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: `${color}1f`, border: `1px solid ${color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.9rem'
        }}>
            <Icon size={20} color={color} />
        </div>
        <h3 className="headline" style={{ fontSize: '1.4rem', margin: 0, marginBottom: '0.4rem' }}>{title}</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.92rem', margin: 0, lineHeight: 1.55 }}>{body}</p>
    </div>
);

const FEATURE_GROUPS = [
    {
        title: 'Meniu QR & experiența clientului',
        features: [
            { icon: QrCode, title: 'Meniu QR la masă', body: 'Clienții scanează codul de la masă — fără aplicație, fără cont, direct pe telefon.', color: '#F59E0B' },
            { icon: Smartphone, title: 'Comenzi de la masă', body: 'Coș, variante de porție, note speciale și trimitere comandă live către echipă.', color: '#14F195' },
            { icon: Languages, title: 'Meniu multilingv', body: 'Clienții aleg limba meniului (RO, EN și altele) cu steaguri vizuale.', color: '#00D9FF' },
            { icon: Globe, title: 'Traducere automată', body: 'Numele, descrierile și categoriile se traduc automat din română la salvare.', color: '#FF2E92' },
            { icon: Info, title: 'Panou Info restaurant', body: 'Program, adresă, Wi-Fi, Google Maps, TripAdvisor, rezervări, WhatsApp și rețele sociale.', color: '#F59E0B' },
            { icon: Wheat, title: 'Alergeni & nutriție', body: 'Afișare alergeni, etichete și valori nutriționale direct în meniul client.', color: '#14F195' },
            { icon: HandHelping, title: 'Cheamă ospătar', body: 'Clientul poate solicita ajutor de la masă cu un singur tap — alertă instant la echipă.', color: '#00D9FF' },
            { icon: Receipt, title: 'Cere nota de plată', body: 'Cerere de notă din meniul QR, vizibilă imediat la ospătar și manager.', color: '#FF2E92' },
            { icon: Plus, title: 'Extra după servire', body: 'Clienții pot adăuga produse după ce comanda a fost servită — evidențiate separat pentru echipă.', color: '#F59E0B' },
        ],
    },
    {
        title: 'Meniu, brand & setări restaurant',
        features: [
            { icon: UtensilsCrossed, title: 'Gestionare meniu', body: 'Categorii, produse, variante, prețuri, ordine și disponibilitate — totul din panou.', color: '#F59E0B' },
            { icon: Layers, title: 'Subcategorii (2 nivele)', body: 'Organizezi meniul pe categorii și subcategorii — Băuturi → Calde / Reci / Vin / Bere. Meniu structurat, clienți care găsesc rapid.', color: '#14F195' },
            { icon: Wand2, title: 'Import meniu cu AI', body: 'Încarci PDF-ul sau o poză cu meniul tipărit, iar AI-ul îl transformă automat într-un meniu digital. Verifici, editezi în preview, salvezi. Funcție Pro.', color: '#FF2E92' },
            { icon: FileSpreadsheet, title: 'Export & import Excel', body: 'Descarci meniul ca Excel, editezi în Office sau Google Sheets, reîncarci. Plus un model gata de completat cu instrucțiuni pas-cu-pas.', color: '#00D9FF' },
            { icon: Image, title: 'Imagini produse', body: 'Upload foto pentru fiecare produs — meniul arată profesional pe telefonul clientului.', color: '#14F195' },
            { icon: Store, title: 'Logo & copertă', body: 'Branding personalizat în header-ul meniului QR — logo și imagine cover restaurant.', color: '#00D9FF' },
            { icon: Clock, title: 'Program de funcționare', body: 'Configurezi zilele și orele — clienții văd când ești deschis în meniul QR.', color: '#FF2E92' },
            { icon: EyeOff, title: 'Ascunde temporar produse', body: 'Marchezi produsele sezoniere sau cu stoc epuizat ca ascunse — dispar din meniul clientului dar rămân în baza ta de date pentru reactivare.', color: '#F59E0B' },
            { icon: Sparkles, title: 'Valori nutriționale cu AI', body: 'Generezi automat calorii, proteine, carbohidrați și grăsimi pentru fiecare produs — cu un singur click, direct din panou.', color: '#F59E0B' },
        ],
    },
    {
        title: 'Sală, mese & comenzi',
        features: [
            { icon: Armchair, title: 'Mese & coduri QR', body: 'Adaugi mese pe zone, generezi QR unic per masă și descarci PNG pentru print.', color: '#F59E0B' },
            { icon: UserCheck, title: 'Asignare ospătari', body: 'Repartizezi mesele manual sau în masă — fiecare ospătar vede doar comenzile lui.', color: '#14F195' },
            { icon: ClipboardList, title: 'Flux complet comenzi', body: 'Pending → Confirmat → Bucătărie → Gata → Servit → Plătit. Control total al stării.', color: '#00D9FF' },
            { icon: Headphones, title: 'Vedere ospătar', body: 'Panou dedicat cu acțiuni rapide: confirmă, trimite la bucătărie, servește, încasează.', color: '#FF2E92' },
            { icon: Edit3, title: 'Editare comenzi', body: 'Echipa poate modifica comenzile active — adaugă, șterge sau ajustează produse.', color: '#F59E0B' },
            { icon: Split, title: 'Împărțire notă', body: 'Împarți nota pe mai mulți clienți direct din panoul de comenzi.', color: '#14F195' },
            { icon: Bell, title: 'Notificări live', body: 'WebSocket — comenzi noi, actualizări și alerte ospătar în timp real, fără refresh.', color: '#00D9FF' },
            { icon: CheckSquare, title: 'Operații în masă', body: 'Selectezi mai multe produse cu o singură acțiune și le muți în altă categorie sau le ștergi pe toate odată — perfect pentru reorganizări.', color: '#FF2E92' },
        ],
    },
    {
        title: 'Bucătărie & echipă',
        features: [
            { icon: ChefHat, title: 'Kitchen Display (KDS)', body: 'Kanban bucătărie cu coloane, timere pe ticket și alertă vizuală la comenzi întârziate.', color: '#FF2E92' },
            { icon: Users, title: 'Management echipă', body: 'Adaugi ospătari, bucătari și manageri — roluri, conturi și permisiuni separate.', color: '#F59E0B' },
            { icon: KeyRound, title: 'Login cu PIN', body: 'Ospătar și bucătărie se autentifică rapid pe tabletă sau mobil cu PIN de 4–8 cifre.', color: '#14F195' },
        ],
    },
    {
        title: 'Lanțuri, multi-locație & onboarding',
        features: [
            { icon: Building2, title: 'Multi-locație pentru lanțuri', body: 'Gestionezi mai multe restaurante sub un singur cont owner. Comutare instantă între locații, abonament separat per locație.', color: '#F59E0B' },
            { icon: Copy, title: 'Copiază meniu între locații', body: 'Deschizi o locație nouă? Copiezi meniul dintr-o locație existentă cu un click — categorii, subcategorii, produse, traduceri. Zero AI, zero cost.', color: '#14F195' },
            { icon: Compass, title: 'Onboarding ghidat', body: 'Restaurantul nou primește un checklist interactiv pe dashboard: setează brand, adaugă produse, generează QR, invită echipa. Pas-cu-pas.', color: '#00D9FF' },
        ],
    },
    {
        title: 'Statistici, securitate & platformă',
        features: [
            { icon: LayoutGrid, title: 'Dashboard KPI', body: 'Privire de ansamblu: comenzi active, mese ocupate, venituri și activitate zilnică.', color: '#F59E0B' },
            { icon: BarChart3, title: 'Statistici avansate', body: 'Venituri pe 14 zile, top produse și insight-uri pentru decizii de business.', color: '#14F195' },
            { icon: TrendingUp, title: 'Planuri & limite', body: 'Standard & Pro — funcții și limite clare (mese, echipă, limbi).', color: '#00D9FF' },
            { icon: Shield, title: 'Autentificare 2FA', body: 'Protecție suplimentară pentru admini cu Google Authenticator — cod unic generat pe telefon.', color: '#FF2E92' },
            { icon: Zap, title: 'Date securizate & izolate', body: 'Fiecare restaurant funcționează izolat — datele tale rămân separate, cu acces pe roluri (owner, manager, ospătar, bucătar).', color: '#14F195' },
        ],
    },
];

export default function Landing() {
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { settings: site, platformName } = useSiteSettings();
    const { plans: PLANS } = useLivePlans();
    const closeMenu = () => setMobileMenuOpen(false);

    return (
        <div className="grain landing-page" style={{ minHeight: '100vh', background: 'var(--bg)' }} data-testid="landing-page">
            {/* Nav — full set on desktop, hamburger + "Începe" on mobile */}
            <nav className="glass landing-nav">
                <div className="container-x landing-nav__inner">
                    <Logo
                        size="lg"
                        logoUrl={site.logo_url}
                        platformName={platformName}
                    />
                    <div className="landing-nav__links">
                        <a href="#pricing" className="btn btn-ghost landing-nav__link--desktop" data-testid="nav-pricing">Planuri</a>
                        <a href="#features" className="btn btn-ghost landing-nav__link--desktop" data-testid="nav-features">Funcționalități</a>
                        <Link to="/contact" className="btn btn-ghost landing-nav__link--desktop" data-testid="nav-contact">Contact</Link>
                        <Link to="/login" className="btn btn-ghost btn-sm landing-nav__link--desktop" data-testid="nav-login">Login</Link>
                        <Link to="/register" className="btn btn-primary btn-sm" data-testid="nav-register">Începe gratuit</Link>
                        <button type="button" className="landing-nav__burger" onClick={() => setMobileMenuOpen(true)}
                                aria-label="Deschide meniul" data-testid="nav-burger">
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile menu overlay */}
            {mobileMenuOpen && (
                <>
                    <div className="landing-mobile-menu__backdrop" onClick={closeMenu} data-testid="nav-backdrop" />
                    <div className="landing-mobile-menu" data-testid="nav-mobile-menu">
                        <div className="landing-mobile-menu__head">
                            <span style={{ fontWeight: 600 }}>Meniu</span>
                            <button type="button" onClick={closeMenu} aria-label="Închide meniul" className="landing-mobile-menu__close">
                                <X size={22} />
                            </button>
                        </div>
                        <a href="#pricing" className="landing-mobile-menu__link" onClick={closeMenu}>Planuri</a>
                        <a href="#features" className="landing-mobile-menu__link" onClick={closeMenu}>Funcționalități</a>
                        <Link to="/contact" className="landing-mobile-menu__link" onClick={closeMenu}>Contact</Link>
                        <Link to="/login" className="landing-mobile-menu__link" onClick={closeMenu}>Login</Link>
                        <Link to="/register" className="btn btn-primary" style={{ marginTop: 8, justifyContent: 'center' }} onClick={closeMenu}>
                            Începe gratuit <ArrowRight size={16} />
                        </Link>
                    </div>
                </>
            )}

            {/* Hero */}
            <section className="landing-hero">
                <div className="container-x landing-hero__inner fade-in-up">
                    <div className="landing-hero__kicker">
                        <span className="live-dot" /> Restaurant tech, reimaginat
                    </div>
                    <h1 className="headline landing-hero__title">
                        Operează-ți restaurantul <br />
                        <em>ca o orchestră</em>.
                    </h1>
                    <p className="landing-hero__subtitle">
                        Meniu QR, comenzi live, bucătărie, ospătari, statistici, echipă, traduceri, branding — o platformă completă pentru restaurantul tău.
                    </p>
                    <div className="landing-hero__actions">
                        <Link to="/register" className="btn btn-primary" data-testid="hero-cta-register">
                            Începe gratuit <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="landing-section" style={{ padding: '4rem 0' }}>
                <div className="container-x">
                    <div style={{ maxWidth: 760, marginBottom: '2.5rem', textAlign: 'center', marginLeft: 'auto', marginRight: 'auto' }}>
                        <div className="landing-section__kicker">Funcționalități</div>
                        <h2 className="headline landing-section__title" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', margin: '0.4rem 0 0.6rem' }}>
                            Tot ce ai nevoie. Nimic în plus.
                        </h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '1.05rem', lineHeight: 1.6, margin: 0 }}>
                            De la scanarea QR-ului la masă până la statisticile din dashboard — iată tot ce face {APP_NAME}.
                        </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.8rem' }}>
                        {FEATURE_GROUPS.map((group) => (
                            <div key={group.title}>
                                <h3 className="headline" style={{ fontSize: '1.35rem', margin: '0 0 1rem', color: 'var(--brand-teal-light)' }}>
                                    {group.title}
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.2rem' }}>
                                    {group.features.map((f) => (
                                        <Feature key={f.title} {...f} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="landing-section" style={{ padding: '4rem 0' }}>
                <div className="container-x">
                    <div style={{ maxWidth: 700, marginBottom: '2.5rem', textAlign: 'center', marginLeft: 'auto', marginRight: 'auto' }}>
                        <div className="landing-section__kicker">Planuri</div>
                        <h2 className="headline landing-section__title" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', margin: '0.4rem 0' }}>
                            Prețuri transparente. Zero capcane.
                        </h2>
                        <p style={{ color: 'var(--text-muted)', maxWidth: 540, margin: '0.6rem auto 0' }}>
                            Prețuri de la {Math.min(...PLANS.map((p) => p.priceRon))} RON + TVA / lună. Lunar și 3 luni — același tarif. Reduceri la 6 luni și anual.
                        </p>
                    </div>
                    <BillingCyclePicker value={billingCycle} onChange={setBillingCycle} />
                    <p className="plan-pricing-note">
                        {PLANS.map((p) => (
                            p.originalPriceRon && p.originalPriceRon > p.priceRon
                                ? `${p.name} ${p.priceRon} RON (redus de la ${p.originalPriceRon} RON)`
                                : `${p.name} ${p.priceRon} RON`
                        )).join(' · ')} — toate + TVA.
                    </p>
                    <div className="plan-grid">
                        {PLANS.map((p) => (
                            <Plan key={p.id} plan={p} testId={`plan-${p.id}`} billingCycle={billingCycle} />
                        ))}
                    </div>
                </div>
            </section>

            <TestimonialMarquee />

            {/* Final CTA */}
            <section style={{ padding: '4rem 0 6rem' }}>
                <div className="container-x">
                    <div className="panel glass" style={{ padding: '3rem', textAlign: 'center', border: '1px solid rgba(245,158,11,0.3)' }}>
                        <h2 className="headline" style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', margin: 0, marginBottom: '0.6rem' }}>
                            Gata să servești <em style={{ color: 'var(--amber)' }}>mai bine</em>?
                        </h2>
                        <p style={{ color: 'var(--text-muted)', maxWidth: 520, margin: '0 auto 1.4rem' }}>
                            {site.trial_days} zile gratuit. Card de credit nu e necesar.
                        </p>
                        <Link to="/register" className="btn btn-primary" style={{ padding: '0.95rem 1.6rem' }} data-testid="footer-cta">
                            Începe acum <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            <SitePublicFooter site={site} />
        </div>
    );
}
